"use client";

import { CartItemType } from "@/app/types/cart";
import { ProductDetailType } from "@/app/types/product-site";
import { toRial } from "./dataLayer";

/**
 * Builds the items[] entries GA4 reads.
 *
 * item_id is the variant id, never the site product, because the variant is
 * what carries the price, the barcode and the stock — it is the thing that was
 * actually bought. The site product rides along as site_product_id so a report
 * can roll every colour of one frame back up into a single product page.
 */

export type Ga4Item = {
    item_id: string;
    item_name: string;
    item_brand?: string;
    item_category?: string;
    item_variant?: string;
    // Left out entirely for a product with no price — an inquiry-only frame or
    // one that is out of stock. A zero would read as "free" in the report.
    price?: number;
    quantity: number;
    discount?: number;
    index?: number;
    item_list_id?: string;
    item_list_name?: string;
    site_product_id?: string;
};

type ItemContext = {
    brand?: string | null;
    siteProductId?: number | string | null;
    quantity?: number;
    index?: number;
    listId?: string;
    listName?: string;
};

/**
 * Colour and size together, which is what separates one variant from another.
 */
function variantLabel(detail: Pick<ProductDetailType, "color_code" | "size">): string | undefined {
    const parts = [detail.color_code, detail.size].filter(Boolean);

    return parts.length ? parts.join(" / ") : undefined;
}

/**
 * Drops empty keys so GA4 never receives a field with nothing in it.
 */
function clean(item: Ga4Item): Ga4Item {
    return Object.fromEntries(
        Object.entries(item).filter(([, value]) => value !== undefined && value !== null && value !== ""),
    ) as Ga4Item;
}

/**
 * A product detail as it appears on the product page and in listings.
 */
export function itemFromProductDetail(detail: ProductDetailType, context: ItemContext = {}): Ga4Item {
    const discount = toRial(detail.discount);

    const price = toRial(detail.price);

    return clean({
        item_id: String(detail.product_id),
        item_name: detail.name,
        item_brand: context.brand ?? undefined,
        item_category: detail.product_class?.title,
        item_variant: variantLabel(detail),
        price: price > 0 ? price : undefined,
        quantity: context.quantity ?? 1,
        discount: discount > 0 ? discount : undefined,
        index: context.index,
        item_list_id: context.listId,
        item_list_name: context.listName,
        site_product_id: context.siteProductId ? String(context.siteProductId) : undefined,
    });
}

/**
 * A line of the server cart, which the API returns in its own shape.
 */
export function itemFromCartItem(item: CartItemType, context: ItemContext = {}): Ga4Item {
    const product = item.product;
    const discount = toRial(product?.discount);

    return clean({
        item_id: String(product?.product_id ?? item.id),
        item_name: item.product_name ?? product?.name,
        item_brand: context.brand ?? item.site_product?.brand ?? undefined,
        item_category: undefined,
        item_variant: product ? variantLabel(product) : undefined,
        price: toRial(item.price ?? product?.price) || undefined,
        quantity: item.quantity ?? 1,
        discount: discount > 0 ? discount : undefined,
        index: context.index,
        site_product_id: item.site_product?.id ? String(item.site_product.id) : undefined,
    });
}

/**
 * Adds up what a set of items is worth, which is the value GA4 wants on cart
 * and checkout events.
 */
export function itemsValue(items: Ga4Item[]): number {
    return items.reduce((total, item) => {
        const unit = (item.price ?? 0) - (item.discount ?? 0);

        return total + unit * (item.quantity ?? 1);
    }, 0);
}

/**
 * Builds an item from loose parts, for the screens that keep the cart in their
 * own shape rather than the shape the API returned. Prices go in as Toman, the
 * way the page holds them.
 */
export function buildItem(parts: {
    variantId: number | string;
    name: string;
    brand?: string | null;
    category?: string | null;
    colorCode?: string | null;
    size?: string | null;
    tomanPrice?: number | null;
    tomanDiscount?: number | null;
    quantity?: number;
    siteProductId?: number | string | null;
    index?: number;
    listId?: string;
    listName?: string;
}): Ga4Item {
    const discount = toRial(parts.tomanDiscount);
    const price = toRial(parts.tomanPrice);
    const variant = [parts.colorCode, parts.size].filter(Boolean).join(" / ");

    return clean({
        item_id: String(parts.variantId),
        item_name: parts.name,
        item_brand: parts.brand ?? undefined,
        item_category: parts.category ?? undefined,
        item_variant: variant || undefined,
        price: price > 0 ? price : undefined,
        quantity: parts.quantity ?? 1,
        discount: discount > 0 ? discount : undefined,
        index: parts.index,
        item_list_id: parts.listId,
        item_list_name: parts.listName,
        site_product_id: parts.siteProductId ? String(parts.siteProductId) : undefined,
    });
}
